import React, { memo } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import Skeleton from "react-loading-skeleton";

import AppSkeletonTheme from "./AppSkeletonTheme";

const centeredLoaderSx = {
  display: "flex",
  justifyContent: "center",
  margin: { xs: 15, sm: 32 },
} as const;

type ProductPlantsListSkeletonProps = {
  /** When true, skip the fake header row (real table header rendered outside). */
  omitDesktopHeadingStrip?: boolean;
};

const plantsRowBorder = "1px solid #c4c4c4";

/** ProductCard — Plants list khi `loadingPf`; bố cục gần giống ItemSize desktop (ảnh + tên | badge | stepper + Add). */
export const ProductPlantsListSkeleton: React.FC<ProductPlantsListSkeletonProps> =
  memo(({ omitDesktopHeadingStrip = false }) => (
    <AppSkeletonTheme borderRadius="10px">
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          py: omitDesktopHeadingStrip ? 0 : 1,
        }}>
        {!omitDesktopHeadingStrip ? (
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              gap: "10px",
              height: 47,
              px: 0,
              borderBottom: plantsRowBorder,
              boxSizing: "border-box",
            }}>
            <Box
              sx={{
                width: "39%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <Skeleton width={72} height={16} />
            </Box>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderLeft: plantsRowBorder,
                height: "100%",
              }}>
              <Skeleton width={120} height={16} />
            </Box>
            <Box
              sx={{
                width: "23%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderLeft: plantsRowBorder,
                height: "100%",
              }}>
              <Skeleton width={40} height={16} />
            </Box>
          </Box>
        ) : null}
        {Array.from({ length: 8 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { sm: "center" },
              gap: { xs: 1.25, sm: "10px" },
              py: { xs: 1.5, sm: "17px" },
              borderBottom: plantsRowBorder,
              boxSizing: "border-box",
            }}>
            <Box
              sx={{
                width: { xs: "100%", sm: "39%" },
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}>
              <Box
                sx={{
                  width: 56,
                  height: 68,
                  flexShrink: 0,
                  borderRadius: "4px",
                  overflow: "hidden",
                  lineHeight: 0,
                }}>
                <Skeleton width={56} height={68} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, maxWidth: { sm: 340 } }}>
                <Skeleton height={16} borderRadius={4} />
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                pl: { xs: 0, sm: "20px" },
                borderLeft: { xs: "none", sm: plantsRowBorder },
              }}
            >
              <Box
                sx={{
                  borderRadius: "5px",
                  overflow: "hidden",
                  lineHeight: 0,
                  boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.12)",
                }}
              >
                <Skeleton width={172} height={34} />
              </Box>
            </Box>
            <Box
              sx={{
                width: { xs: "100%", sm: "23%" },
                flexShrink: 0,
                display: "flex",
                justifyContent: { xs: "flex-end", sm: "center" },
                alignItems: "center",
                pr: { sm: "10px" },
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  lineHeight: 0,
                }}
              >
                <Skeleton circle width={32} height={32} />
                <Skeleton width={68} height={32} style={{ borderRadius: 6 }} />
                <Skeleton circle width={32} height={32} />
                <Box sx={{ width: 36, pl: 0.25, lineHeight: 0 }}>
                  <Skeleton height={14} width={36} />
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </AppSkeletonTheme>
  ));

ProductPlantsListSkeleton.displayName = "ProductPlantsListSkeleton";

/** Chi tiết sản phẩm (ItemDetail). */
export const ProductDetailSkeleton: React.FC = memo(() => (
  <AppSkeletonTheme borderRadius="12px">
    <Box
      sx={{
        margin: { xs: 2, sm: 3 },
        maxWidth: 1200,
        mx: "auto",
        px: { xs: 1, sm: 2 },
      }}
    >
      <Skeleton width={100} height={36} borderRadius={1} style={{ marginBottom: 16 }} />
      <Box
        sx={{
          display: { xs: "block", md: "flex" },
          gap: 3,
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ width: { xs: "100%", md: "48%" }, flexShrink: 0 }}>
          <Skeleton height={320} borderRadius={2} />
          <Box sx={{ display: "flex", gap: 1, mt: 1.5, justifyContent: "center" }}>
            <Skeleton width={56} height={56} borderRadius={1} />
            <Skeleton width={56} height={56} borderRadius={1} />
            <Skeleton width={56} height={56} borderRadius={1} />
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, mt: { xs: 2, md: 0 } }}>
          <Skeleton width="85%" height={28} style={{ marginBottom: 12 }} />
          <Skeleton width={140} height={32} style={{ marginBottom: 16 }} />
          <Skeleton width="100%" height={14} count={2} style={{ marginBottom: 8 }} />
          <Skeleton width="60%" height={20} style={{ marginBottom: 20 }} />
          <Skeleton width="100%" height={120} borderRadius={1} style={{ marginBottom: 16 }} />
          <Skeleton width="100%" height={48} borderRadius={1} />
        </Box>
      </Box>
    </Box>
  </AppSkeletonTheme>
));

ProductDetailSkeleton.displayName = "ProductDetailSkeleton";

/** About Us — cột chữ + ảnh. */
export const AboutUsPageSkeleton: React.FC = memo(() => (
  <AppSkeletonTheme borderRadius="12px">
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, maxWidth: 1200, mx: "auto" }}>
      <Skeleton width={160} height={32} style={{ marginBottom: 24 }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Skeleton height={16} style={{ marginBottom: 10 }} count={4} />
          <Skeleton width="95%" height={16} style={{ marginBottom: 10 }} />
          <Skeleton width="88%" height={16} />
        </Box>
        <Box sx={{ display: { xs: "none", md: "block" }, width: "50%" }}>
          <Skeleton height={400} borderRadius={2} />
        </Box>
      </Box>
    </Box>
  </AppSkeletonTheme>
));

AboutUsPageSkeleton.displayName = "AboutUsPageSkeleton";

/** Order history — bảng. */
export const OrderHistoryPageSkeleton: React.FC = memo(() => (
  <AppSkeletonTheme borderRadius="8px">
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, maxWidth: 900, mx: "auto" }}>
      <Skeleton width={200} height={26} style={{ margin: "0 auto 20px" }} />
      <Skeleton height={40} style={{ marginBottom: 8 }} />
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} height={48} style={{ marginBottom: 6 }} />
      ))}
    </Box>
  </AppSkeletonTheme>
));

OrderHistoryPageSkeleton.displayName = "OrderHistoryPageSkeleton";

/** Order detail. */
export const OrderHistoryDetailPageSkeleton: React.FC = memo(() => (
  <AppSkeletonTheme borderRadius="8px">
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, maxWidth: 900, mx: "auto" }}>
      <Skeleton width="55%" height={24} style={{ marginBottom: 16 }} />
      <Skeleton height={56} style={{ marginBottom: 20 }} />
      <Skeleton width="100%" height={180} borderRadius={1} style={{ marginBottom: 20 }} />
      <Skeleton height={36} style={{ marginBottom: 8 }} />
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} height={44} style={{ marginBottom: 4 }} />
      ))}
    </Box>
  </AppSkeletonTheme>
));

OrderHistoryDetailPageSkeleton.displayName = "OrderHistoryDetailPageSkeleton";

/** Notifications list. */
export const NotificationListPageSkeleton: React.FC = memo(() => (
  <AppSkeletonTheme borderRadius="10px">
    <Box
      sx={{
        maxWidth: 600,
        mx: "auto",
        px: { xs: 2, sm: 3 },
        py: 2,
        bgcolor: "#fff",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Skeleton circle width={36} height={36} />
        <Skeleton width="40%" height={22} />
        <Box sx={{ flex: 1 }} />
        <Skeleton circle width={36} height={36} />
      </Box>
      {Array.from({ length: 8 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            gap: 1.5,
            py: 1.5,
            borderBottom: "1px solid #eee",
          }}
        >
          <Skeleton circle width={40} height={40} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton width="75%" height={16} style={{ marginBottom: 8 }} />
            <Skeleton width="55%" height={14} />
          </Box>
        </Box>
      ))}
    </Box>
  </AppSkeletonTheme>
));

NotificationListPageSkeleton.displayName = "NotificationListPageSkeleton";

/** Account — đang xử lý (vd. logout). */
export const AccountActionSkeleton: React.FC = memo(() => (
  <AppSkeletonTheme borderRadius="12px">
    <Box sx={centeredLoaderSx}>
      <Box
        sx={{
          width: "100%",
          maxWidth: 320,
          p: 3,
          borderRadius: 2,
          bgcolor: "#fff",
          boxShadow: 2,
        }}
      >
        <Skeleton circle width={80} height={80} style={{ margin: "0 auto 16px" }} />
        <Skeleton height={14} count={3} style={{ marginBottom: 8 }} />
      </Box>
    </Box>
  </AppSkeletonTheme>
));

AccountActionSkeleton.displayName = "AccountActionSkeleton";

/** Modal giỏ hàng khi gọi API. */
type CartLoadingModalSkeletonProps = { open: boolean };

export const CartLoadingModalSkeleton: React.FC<CartLoadingModalSkeletonProps> =
  memo(({ open }) => (
    <Modal open={open} aria-labelledby="cart-loading-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          outline: "none",
        }}
      >
        <Box
          sx={{
            bgcolor: "#fff",
            borderRadius: 2,
            px: 4,
            py: 3.5,
            minWidth: { xs: 260, sm: 300 },
            maxWidth: "90vw",
            boxShadow: 8,
            textAlign: "center",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#007f18", mb: 2 }} />
          <Typography
            id="cart-loading-title"
            component="p"
            sx={{ fontWeight: 600, fontSize: "1rem", color: "#1a1a1a", m: 0 }}
          >
            Placing your order…
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 1, color: "text.secondary", lineHeight: 1.4 }}
          >
            Please wait a moment.
          </Typography>
        </Box>
      </Box>
    </Modal>
  ));

CartLoadingModalSkeleton.displayName = "CartLoadingModalSkeleton";

/** Best sellers trên Cart. */
export const BestSellersStripSkeleton: React.FC = memo(() => (
  <AppSkeletonTheme borderRadius="12px">
    <Box sx={{ py: 2, width: "100%" }}>
      <Skeleton width={180} height={24} style={{ margin: "0 auto 16px" }} />
      <Box sx={{ display: "flex", gap: 2, overflow: "hidden", px: 1 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Box key={i} sx={{ flex: "0 0 160px", minWidth: 160 }}>
            <Skeleton height={140} borderRadius={2} />
            <Skeleton width="90%" height={14} style={{ marginTop: 10 }} />
            <Skeleton width="50%" height={14} style={{ marginTop: 6 }} />
          </Box>
        ))}
      </Box>
    </Box>
  </AppSkeletonTheme>
));

BestSellersStripSkeleton.displayName = "BestSellersStripSkeleton";
